import { ViewToggle, type ActiveView } from './ViewToggle';
import { LayerSelector, type LayerOption } from './LayerSelector';

interface AppHeaderProps {
  activeView: ActiveView;
  onViewToggle: (view: ActiveView) => void;
  activeLayer: LayerOption;
  onLayerChange: (layer: LayerOption) => void;
  title?: string;
  showReplay?: boolean;
  onReplay?: () => void;
  disabled?: boolean;
  onLogoClick?: () => void; // Story 8-5: Reset to default view
  // Epic 9: Time Machine
  onTimeMachine?: () => void;
  isTimeMachineMode?: boolean;
}

export function AppHeader({
  activeView,
  onViewToggle,
  activeLayer,
  onLayerChange,
  title = 'Our Love Map',
  showReplay = false,
  onReplay,
  disabled = false,
  onLogoClick,
  onTimeMachine,
  isTimeMachineMode = false,
}: AppHeaderProps) {
  return (
    <header className="fixed top-0 left-0 right-0 z-[1000] bg-bg-base/90 backdrop-blur-sm border-b border-gray-800">
      {/* Main header row */}
      <div className="flex items-center justify-between h-12 lg:h-16 px-4 lg:px-6">
        {/* Title - Left (Story 8-5: Clickable to reset) */}
        <h1 className="text-lg lg:text-xl font-semibold text-white shrink-0">
          {onLogoClick ? (
            <button
              type="button"
              onClick={onLogoClick}
              className="hover:text-primary transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/50 rounded px-1 -mx-1"
              aria-label="Reset to default view"
            >
              {title}
            </button>
          ) : (
            title
          )}
        </h1>

        {/* Layer Selector - Center (visible on desktop) */}
        <div className="hidden lg:flex">
          <LayerSelector
            activeLayer={activeLayer}
            onLayerChange={onLayerChange}
            disabled={disabled}
          />
        </div>

        {/* View Toggle and Replay - Right */}
        <div className="flex items-center gap-2">
          <ViewToggle
            activeView={activeView}
            onToggle={onViewToggle}
            disabled={disabled}
          />

          {/* Time Machine button (Epic 9) */}
          {onTimeMachine && !isTimeMachineMode && (
            <button
              onClick={onTimeMachine}
              disabled={disabled}
              className="px-3 py-2 min-h-11 text-sm text-gray-400 hover:text-white bg-bg-base/80 hover:bg-bg-base border border-gray-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Enter Time Machine mode"
            >
              <span className="hidden sm:inline">Time Machine</span>
              <span className="sm:hidden">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </span>
            </button>
          )}

          {showReplay && onReplay && (
            <button
              onClick={onReplay}
              className="px-3 py-2 min-h-11 text-sm text-gray-400 hover:text-white bg-bg-base/80 hover:bg-bg-base border border-gray-700 rounded-md transition-colors"
              aria-label="Replay opening animation"
            >
              <span className="hidden sm:inline">Replay</span>
              <span className="sm:hidden">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Layer Selector Row - Mobile only */}
      <div className="lg:hidden flex justify-center px-4 pb-2">
        <LayerSelector
          activeLayer={activeLayer}
          onLayerChange={onLayerChange}
          disabled={disabled}
        />
      </div>
    </header>
  );
}

export type { ActiveView };
